import React, { useEffect } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  CompositeNavigationProp,
  useNavigation,
  useRoute,
  RouteProp,
} from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Platform } from 'react-native';
import Snackbar from '~/components/Snackbar';
import { useAuth } from '~/context/auth';
import { useIntercom } from '~/context/intercom';
import { NO_PAG_LIST_OFFSET } from '~/data/constants';
import { Group, GroupUser } from '~/data/models/group';
import {
  GROUP_USERS,
  REMOVE_USER_FROM_GROUP,
  USER_GROUP,
} from '~/data/operations/group';
import { getRefreshToken, getToken } from '~/data/storage';
import { GroupsUserGroupStatusChoices } from '~/data/types/graphql';
import { LobbyStackParamList, RootStackParamList } from '~/navigation/types';
import {
  redirectToCC,
  redirectToLayoutCreatorWithToken,
} from '~/utils/helpers';
import { t } from '~/utils/i18n';
import LobbyLayout from './layout';

type LobbyNavProp = CompositeNavigationProp<
  StackNavigationProp<LobbyStackParamList, 'Lobby'>,
  StackNavigationProp<RootStackParamList>
>;

type LobbyRouteProp = RouteProp<LobbyStackParamList, 'Lobby'>;

export default function Lobby(): JSX.Element {
  const navigation = useNavigation<LobbyNavProp>();
  const { onMessenger } = useIntercom();

  const route = useRoute<LobbyRouteProp>();
  const hasCcParam = route.params?.cc;
  const hasLayoutCreatorParam = route.params?.layoutcreator;

  const { authUserId, authUser, loading, onAuthGroup, onLogout } = useAuth();
  const [loadGroupUsers] = useLazyQuery(GROUP_USERS);

  const firstName = authUser?.firstName || '';

  const {
    loading: loadingGroupUsers,
    error: groupUsersError,
    data: groupUsersData,
    refetch: refetchGroupUsers,
  } = useQuery(GROUP_USERS, {
    skip: !authUserId,
    variables: {
      user: authUserId,
      first: NO_PAG_LIST_OFFSET,
    },
  });

  const [removeUserFromGroup] = useMutation(REMOVE_USER_FROM_GROUP);

  const groupUsers: GroupUser[] =
    (groupUsersData?.userGroups?.edges.map(
      (edge) => edge?.node,
    ) as GroupUser[]) || [];

  const groupUsersAccepted: GroupUser[] = groupUsers.filter(
    (userGroup) => userGroup.status === 'ACCEPTED',
  );
  const groupUsersNotAccepted: GroupUser[] = groupUsers.filter(
    (userGroup) =>
      userGroup.status === 'PENDING' || userGroup.status === 'REJECTED',
  );

  const userGroupPending = groupUsersNotAccepted.find(
    (userGroup) => userGroup.status === 'PENDING',
  );

  useQuery(USER_GROUP, {
    skip: !userGroupPending,
    variables: {
      id: userGroupPending?.id as string,
    },
    pollInterval: 5000,
  });

  const onSelectedGroup = async (
    authGroupId: string,
    authUserGroupId: string,
  ) => {
    onAuthGroup(authGroupId, authUserGroupId);
  };

  async function onRemoveRequest(groupId: string) {
    try {
      await removeUserFromGroup({
        variables: {
          input: { groupId, userId: authUserId },
        },
        refetchQueries: [
          {
            query: GROUP_USERS,
            variables: {
              user: authUserId,
              first: NO_PAG_LIST_OFFSET,
            },
          },
        ],
      });
      Snackbar.show(t('lobby.requestCancelled'));
    } catch (e) {
      if (e instanceof Error) {
        Snackbar.show(e.message);
      }
    }
  }

  useEffect(() => {
    (async () => {
      try {
        if (Platform.OS === 'web' && authUser) {
          if (hasLayoutCreatorParam) {
            const token = await getToken();
            const refreshToken = await getRefreshToken();

            token &&
              refreshToken &&
              redirectToLayoutCreatorWithToken({ token, refreshToken });
            return;
          }

          if (hasCcParam) {
            const { data: groupUsersData } = await loadGroupUsers({
              variables: {
                user: authUserId,
                first: NO_PAG_LIST_OFFSET,
                status: GroupsUserGroupStatusChoices.ACCEPTED,
              },
            });
            const groups =
              (groupUsersData?.userGroups?.edges.map(
                (edge) => edge?.node?.group,
              ) as Group[]) || null;

            if (authUser && groups) {
              redirectToCC(authUser, groups);
            }
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          Snackbar.show(e.message);
        }
      }
    })();
  }, [authUser]);

  return (
    <LobbyLayout
      loading={loading || loadingGroupUsers}
      errorMessage={(groupUsersError && groupUsersError.message) || undefined}
      userName={firstName}
      groupUsersAccepted={groupUsersAccepted}
      groupUsersPending={groupUsersNotAccepted}
      onLogout={() => onLogout()}
      onJoinGroup={() =>
        navigation.navigate('GroupStack', {
          screen: 'JoinSelectLocation',
        })
      }
      onMessenger={onMessenger}
      onSelectedGroup={(groupId, userGroupId) =>
        onSelectedGroup(groupId, userGroupId)
      }
      onRemoveRequest={(groupId) => onRemoveRequest(groupId)}
      onRetry={() => {
        refetchGroupUsers();
      }}
      onRefresh={() => {
        refetchGroupUsers();
      }}
    />
  );
}

/**
 * TODO:
 * - Setup no group lottie
 * - Handle multiple groups
 * - Handle request overview
 */
